<script setup lang="ts">
/**
 * Cloche notifications dans le header (Phase 1).
 *
 * Affiche :
 *  - Icone Bell avec badge nombre non-lues (max "9+")
 *  - Au clic : dropdown des 10 dernieres notifications
 *  - Click sur une notification : mark_read + navigation vers le link
 *  - Action "Tout marquer comme lu"
 *  - Lien vers /preferences/notifications (icone engrenage)
 *
 * Polling 30s gere par le store useNotificationsStore (startPolling()
 * appele depuis l'app au login).
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Bell, Check, Settings as SettingsIcon, X } from 'lucide-vue-next';
import { useNotificationsStore } from '../stores/notifications';
import type { AppNotification } from '../types/models';

const router = useRouter();
const { t } = useI18n();
const notifs = useNotificationsStore();
const open = ref(false);
// 2 refs : le wrapper du bouton (pour click sur cloche) + la dropdown elle-meme
// (teleportee au body, donc hors du sous-arbre du bouton). On verifie les 2
// pour fermer correctement au clic exterieur.
const buttonWrapperRef = ref<HTMLElement | null>(null);
const dropdownEl = ref<HTMLElement | null>(null);

function toggle(): void {
  open.value = !open.value;
}

function close(): void {
  open.value = false;
}

async function onClickNotif(n: AppNotification): Promise<void> {
  if (!n.is_read) await notifs.markRead(n.id);
  close();
  if (n.link) router.push(n.link);
}

async function onMarkAllRead(): Promise<void> {
  await notifs.markAllRead();
}

function onPreferences(): void {
  close();
  router.push({ name: 'notification-preferences' });
}

/** Format date relative court (il y a 5 min, hier, 12/05). */
function fmtRelative(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return t('notifications.relative.just_now');
  if (diffMin < 60) return t('notifications.relative.minutes_ago', { n: diffMin });
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return t('notifications.relative.hours_ago', { n: diffH });
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return t('notifications.relative.yesterday');
  if (diffD < 7) return t('notifications.relative.days_ago', { n: diffD });
  return d.toLocaleDateString();
}

// Fermer la dropdown si clic dehors du bouton ET de la dropdown (teleportee)
function onClickOutside(e: MouseEvent): void {
  if (!open.value) return;
  const target = e.target as Node;
  const inButton = buttonWrapperRef.value?.contains(target);
  const inDropdown = dropdownEl.value?.contains(target);
  if (!inButton && !inDropdown) open.value = false;
}

onMounted(() => {
  document.addEventListener('click', onClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside);
});

const badge = computed(() => notifs.badgeLabel);
</script>

<template>
  <div ref="buttonWrapperRef" class="relative">
    <button
      type="button"
      class="relative p-2 rounded-md hover:bg-bg-muted transition-colors"
      :aria-label="t('notifications.bell_aria', { count: notifs.unreadCount })"
      @click="toggle"
    >
      <Bell :size="18" class="text-text-secondary" aria-hidden="true" />
      <span
        v-if="badge"
        class="absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center tabular-nums"
        aria-hidden="true"
      >{{ badge }}</span>
    </button>

    <!-- Dropdown teleporte au body pour echapper au stacking context de
         l'AppHeader (sticky z-200). Sinon la dropdown reste piegee derriere
         les barres de tabs sticky de GestionView/TempsView (bug 2026-05-31).
         Position fixed top-right avec largeur responsive. -->
    <Teleport to="body">
    <div
      v-if="open"
      ref="dropdownEl"
      class="fixed right-2 top-16
             w-[calc(100vw-1rem)] sm:w-96 max-w-sm sm:max-w-none
             max-h-[80vh] flex flex-col bg-card border border-border rounded-md shadow-lg
             z-tooltip overflow-hidden"
      role="dialog"
      :aria-label="t('notifications.dropdown_aria')"
    >
      <!-- Header -->
      <div class="flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
        <h3 class="text-sm font-semibold text-text">{{ t('notifications.title') }}</h3>
        <div class="flex items-center gap-1">
          <button v-if="notifs.hasUnread"
                  type="button"
                  class="p-1 text-xs text-text-secondary hover:text-text rounded"
                  :title="t('notifications.mark_all_read')"
                  @click="onMarkAllRead">
            <Check :size="14" aria-hidden="true" />
          </button>
          <button type="button"
                  class="p-1 text-xs text-text-secondary hover:text-text rounded"
                  :title="t('notifications.preferences')"
                  @click="onPreferences">
            <SettingsIcon :size="14" aria-hidden="true" />
          </button>
          <button type="button"
                  class="p-1 text-text-secondary hover:text-text rounded"
                  :title="t('common.common.close')"
                  @click="close">
            <X :size="14" aria-hidden="true" />
          </button>
        </div>
      </div>

      <!-- Liste -->
      <div class="flex-1 overflow-y-auto">
        <p v-if="notifs.notifications.length === 0"
           class="text-center text-sm text-text-muted py-8 px-4">
          {{ t('notifications.empty') }}
        </p>
        <ul v-else class="divide-y divide-border">
          <li v-for="n in notifs.notifications" :key="n.id">
            <button
              type="button"
              class="w-full text-left px-3 py-2.5 hover:bg-bg-muted transition-colors flex flex-col gap-1"
              :class="!n.is_read ? 'bg-accent/5' : ''"
              @click="onClickNotif(n)"
            >
              <div class="flex items-start justify-between gap-2">
                <span class="text-sm font-medium text-text"
                      :class="!n.is_read ? 'font-semibold' : ''">
                  {{ n.title }}
                </span>
                <span v-if="!n.is_read"
                      class="shrink-0 h-2 w-2 rounded-full bg-accent mt-1.5"
                      aria-hidden="true"></span>
              </div>
              <p class="text-xs text-text-secondary line-clamp-2">{{ n.message }}</p>
              <p class="text-[11px] text-text-muted">{{ fmtRelative(n.created_at) }}</p>
            </button>
          </li>
        </ul>
      </div>
    </div>
    </Teleport>
  </div>
</template>
